import type { CourseData } from '../course/model.js';
import { SCHEMA_VERSION, courseDataSchema } from '../course/model.js';
import { interactionSchema } from '../interaction/model.js';

/**
 * Contract 2 of docs/plans/41-cross-cutting-contracts.md.
 *
 * A migration is a pure `(vN) -> (vN+1)` function. Reading a v3 blob into a v7
 * client runs 3->4->5->6->7. Nothing outside this module branches on version.
 *
 * FORWARD ONLY: the add-in is web-deployed, so there is no down-migration. The
 * failure that model creates instead is a *stale* client meeting a blob from the
 * future, which `migrate` refuses rather than partially downgrading.
 */
export interface Migration {
  /** The version this migration reads. It produces `from + 1`. */
  readonly from: number;
  readonly description: string;
  readonly migrate: (blob: Record<string, unknown>) => Record<string, unknown>;
}

/**
 * Ordered by `from`. Additive changes get explicit migrations too: defaults
 * make a current object convenient to create, but the migration is the evidence
 * that older decks were deliberately handled.
 */
export const MIGRATIONS: readonly Migration[] = [
  {
    from: 1,
    description: 'Add course and per-slide Text-to-Speech authoring state.',
    migrate: (blob) => ({
      ...blob,
      tts: {
        provider: 'elevenlabs',
        voiceId: '',
        modelId: 'eleven_multilingual_v2',
        rate: 1,
        pitch: 0,
        volume: 1,
        pauseAfterSentenceMs: 250,
        syntheticDisclosure: true,
        lexiconVersion: 0,
        lexicon: [],
      },
    }),
  },
  {
    from: 2,
    description: 'Validate the M8 interaction namespace while preserving any pre-release data.',
    migrate: (blob) => {
      const next = structuredCloneCompat(blob);
      const slides = next['slides'];
      if (typeof slides !== 'object' || slides === null || Array.isArray(slides)) return next;
      for (const namespaces of Object.values(slides)) {
        if (typeof namespaces !== 'object' || namespaces === null || Array.isArray(namespaces)) continue;
        const ns = namespaces as Record<string, unknown>;
        if (!Object.prototype.hasOwnProperty.call(ns, 'interaction')) continue;
        const parsed = interactionSchema.safeParse(ns['interaction']);
        if (parsed.success) ns['interaction'] = parsed.data;
        else {
          ns['legacyInteraction'] = ns['interaction'];
          delete ns['interaction'];
        }
      }
      return next;
    },
  },
];

export type MigrationFailure =
  | { readonly reason: 'not-an-object'; readonly message: string }
  | { readonly reason: 'missing-version'; readonly message: string }
  | { readonly reason: 'from-the-future'; readonly message: string; readonly blobVersion: number }
  | {
      readonly reason: 'no-migration-path';
      readonly message: string;
      readonly blobVersion: number;
    }
  | { readonly reason: 'migration-threw'; readonly message: string; readonly atVersion: number }
  | { readonly reason: 'invalid-after-migration'; readonly message: string };

export type MigrationResult =
  | { readonly ok: true; readonly data: CourseData; readonly ranMigrations: readonly number[] }
  /** The original blob is handed back untouched so the caller can preserve it. */
  | { readonly ok: false; readonly failure: MigrationFailure; readonly original: unknown };

export function currentSchemaVersion(): number {
  return SCHEMA_VERSION;
}

export function blobVersionOf(raw: unknown): number | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const v = (raw as Record<string, unknown>)['schemaVersion'];
  return typeof v === 'number' && Number.isInteger(v) ? v : undefined;
}

/**
 * Reads a stored blob of any prior version and returns current-version data.
 * Never mutates the input, and never returns partially-migrated output.
 */
export function migrate(raw: unknown, targetVersion: number = SCHEMA_VERSION): MigrationResult {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return {
      ok: false,
      original: raw,
      failure: { reason: 'not-an-object', message: 'Stored course data is not a JSON object.' },
    };
  }

  const version = blobVersionOf(raw);
  if (version === undefined) {
    return {
      ok: false,
      original: raw,
      failure: {
        reason: 'missing-version',
        message: 'Stored course data has no integer schemaVersion.',
      },
    };
  }

  if (version > targetVersion) {
    return {
      ok: false,
      original: raw,
      failure: {
        reason: 'from-the-future',
        blobVersion: version,
        message:
          'This deck was edited with a newer version of SlideForge. Reload the add-in to get the current build; nothing has been changed.',
      },
    };
  }

  const chain = applyMigrations(raw as Record<string, unknown>, version, targetVersion, MIGRATIONS);
  if (!chain.ok) {
    return { ok: false, original: raw, failure: chain.failure };
  }

  const parsed = courseDataSchema.safeParse(chain.blob);
  if (!parsed.success) {
    return {
      ok: false,
      original: raw,
      failure: {
        reason: 'invalid-after-migration',
        message: parsed.error.issues
          .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
          .join('; '),
      },
    };
  }

  return { ok: true, data: parsed.data, ranMigrations: chain.ran };
}

export type ChainResult =
  | { readonly ok: true; readonly blob: Record<string, unknown>; readonly ran: readonly number[] }
  | { readonly ok: false; readonly failure: MigrationFailure };

/**
 * Runs the version chain without validating the result. Split out from `migrate`
 * so the chaining rules can be exercised against a registry of test migrations —
 * the real registry is empty until the first schema change, and a rule that is
 * only tested once it has already broken something is not a rule.
 */
export function applyMigrations(
  blob: Record<string, unknown>,
  fromVersion: number,
  targetVersion: number,
  registry: readonly Migration[],
): ChainResult {
  let working = structuredCloneCompat(blob);
  const ran: number[] = [];

  for (let v = fromVersion; v < targetVersion; v += 1) {
    const step = registry.find((m) => m.from === v);
    if (!step) {
      return {
        ok: false,
        failure: {
          reason: 'no-migration-path',
          blobVersion: fromVersion,
          message: `No migration is registered from schema version ${v} to ${v + 1}.`,
        },
      };
    }
    try {
      working = step.migrate(working);
      working['schemaVersion'] = v + 1;
      ran.push(v);
    } catch (error) {
      return {
        ok: false,
        failure: {
          reason: 'migration-threw',
          atVersion: v,
          message: error instanceof Error ? error.message : String(error),
        },
      };
    }
  }

  return { ok: true, blob: working, ran };
}

/** True when a blob was written by a newer build than this one. */
export function isFromTheFuture(raw: unknown): boolean {
  const v = blobVersionOf(raw);
  return v !== undefined && v > SCHEMA_VERSION;
}

function structuredCloneCompat<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
